/**
 * The appearance preference (System, Light or Dark), one per person per browser, shared by every Trace surface on
 * the same parent domain (Trace Admin on admin.<domain> and the account hub on app.<domain>).
 *
 * It lives in auth-ui because auth-ui is the browser layer both apps already share (and that product apps vendor):
 * the sign-in frames it styles must turn dark with the pages around them, and its vite-shell entry already owns
 * what runs before the bundle. vite/signing-in-shell.cjs (appearanceBoot) applies the stored choice before the first paint; this
 * module reads, writes and resolves it afterwards. test/appearance.test.ts holds the two to the same answers.
 *
 * Storage: a cookie, because localStorage is per origin and admin.* and app.* are different origins. The cookie
 * carries no identity and no secret (one of three words), so it is readable by script and sent with requests
 * harmlessly. Where no shared parent domain can be derived (localhost, an IP address, a customer domain of the
 * hub) the cookie is host-only; on localhost a host-only cookie is still shared between ports, so the local hub
 * (4710) and Trace Admin (4711) agree too. localStorage mirrors the choice as the fallback for a browser that
 * refuses the cookie.
 */
/** In the order the controls offer them (trace-ui's SetupAppearanceSelector: Light, System, Dark). */
export const APPEARANCE_PREFERENCES = ['light', 'system', 'dark'];
export const APPEARANCE_COOKIE = 'trace_appearance';
export const APPEARANCE_STORAGE_KEY = 'trace.appearance';
/** The attribute (`data-trace-appearance`) trace-ui and the Trace stylesheets key their dark values on. */
export const APPEARANCE_ATTRIBUTE = 'data-trace-appearance';
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';
/** 400 days: the longest lifetime Chrome keeps for a cookie, so a choice is never quietly forgotten sooner. */
export const APPEARANCE_COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;
/** Anything that is not exactly one of the three words is "system" (the default), never an error. */
export function parseAppearance(value) {
    return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}
/** The preference in a `document.cookie` string, or null when the cookie is absent or holds another value. */
export function readAppearanceCookie(cookies) {
    for (const part of cookies.split(';')) {
        const separator = part.indexOf('=');
        if (separator < 0 || part.slice(0, separator).trim() !== APPEARANCE_COOKIE)
            continue;
        const value = part.slice(separator + 1).trim();
        if (value === 'light' || value === 'dark' || value === 'system')
            return value;
    }
    return null;
}
export function resolveAppearance(preference, systemDark) {
    if (preference === 'system')
        return systemDark ? 'dark' : 'light';
    return preference;
}
const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;
/**
 * The Domain attribute for the cookie, or null for a host-only cookie.
 *
 * The shared parent is the hub's host without its first label (app.trace.io gives trace.io, app.dev.trace.io gives
 * dev.trace.io), because every Trace surface is a sibling of the hub there. It is used only when the page's own
 * host is that parent or under it, so a customer domain of the hub (app.northwind.example) keeps its cookie to
 * itself. A parent of fewer than two labels (com, io), localhost, an IP address or anything malformed gives null:
 * the cookie is then never offered to a public suffix. Browsers refuse a public-suffix Domain on their own as well
 * (co.uk from app.co.uk); `storeAppearance` notices that refusal and falls back to a host-only cookie.
 */
export function appearanceCookieDomain(pageHost, hubOrigin) {
    if (!hubOrigin)
        return null;
    let hubHost;
    try {
        hubHost = new URL(hubOrigin).hostname.toLowerCase();
    }
    catch {
        return null;
    }
    const page = pageHost.toLowerCase().replace(/:\d+$/, '');
    if (!page || hubHost.startsWith('[') || page.startsWith('[') || IPV4.test(hubHost) || IPV4.test(page))
        return null;
    const labels = hubHost.split('.');
    if (labels.some((label) => label === ''))
        return null;
    const parent = labels.slice(1);
    if (parent.length < 2 || parent[parent.length - 1] === 'localhost')
        return null;
    const domain = parent.join('.');
    return page === domain || page.endsWith(`.${domain}`) ? domain : null;
}
function defaultEnvironment() {
    let storage;
    try {
        storage = window.localStorage;
    }
    catch {
        // Storage can be blocked (privacy settings, sandboxed frames): the cookie alone carries the choice then.
        storage = null;
    }
    return {
        readCookies: () => document.cookie,
        writeCookie: (line) => {
            document.cookie = line;
        },
        location: window.location,
        storage,
    };
}
/** The stored preference: the cookie first (shared between the apps), then this origin's localStorage. */
export function readStoredAppearance(env = defaultEnvironment()) {
    const fromCookie = readAppearanceCookie(env.readCookies());
    if (fromCookie)
        return fromCookie;
    try {
        return parseAppearance(env.storage?.getItem(APPEARANCE_STORAGE_KEY));
    }
    catch {
        return 'system';
    }
}
/**
 * Stores the preference for every Trace surface on the shared parent domain (see appearanceCookieDomain) and in
 * this origin's localStorage. Returns the Domain written, or null for a host-only cookie.
 */
export function storeAppearance(preference, hubOrigin, env = defaultEnvironment()) {
    const base = `${APPEARANCE_COOKIE}=${preference}; Path=/; Max-Age=${APPEARANCE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${env.location.protocol === 'https:' ? '; Secure' : ''}`;
    let domain = appearanceCookieDomain(env.location.hostname, hubOrigin);
    if (domain) {
        // A host-only cookie left from an earlier fallback would be read before the shared one (same path, older), so
        // it goes first. Without a Domain attribute this deletes only that host-only cookie.
        env.writeCookie(`${APPEARANCE_COOKIE}=; Path=/; Max-Age=0`);
        env.writeCookie(`${base}; Domain=${domain}`);
        // A browser that refuses the Domain (a public suffix it knows and this code does not) drops the cookie
        // silently; a host-only one still keeps the choice on this surface.
        if (readAppearanceCookie(env.readCookies()) !== preference)
            domain = null;
    }
    if (!domain)
        env.writeCookie(base);
    try {
        env.storage?.setItem(APPEARANCE_STORAGE_KEY, preference);
    }
    catch {
        // Storage full or blocked: the cookie carries the choice.
    }
    return domain;
}
/** Puts the resolved appearance on the root element: the attribute the styles key on, and the browser's scheme. */
export function applyAppearance(resolved, root = document.documentElement) {
    root.setAttribute(APPEARANCE_ATTRIBUTE, resolved);
    // Form controls, scroll bars and the canvas behind the page follow it (the boot script sets the same).
    root.style.colorScheme = resolved;
}
//# sourceMappingURL=preference.js.map